// Demo/localStorage data layer for Messaging.
// Mirrors the shape returned by lib/messaging.js in live mode.

const STORE_KEY = "daycare.demo.messaging.v1";
const PARENT_ONBOARDING_MESSAGE_KEY = "daycare.demo.parent-onboarding-message.v1";
const ORG_ID = "00000000-0000-0000-0000-000000000001";
const SUNFLOWER_ID = "00000000-0000-0000-0000-000000000101";
const COHEN_FAMILY_ID = "00000000-0000-0000-0000-000000000201";

function hoursAgo(n) {
  return new Date(Date.now() - n * 60 * 60 * 1000).toISOString();
}
function minutesAgo(n) {
  return new Date(Date.now() - n * 60 * 1000).toISOString();
}

function seedInitial() {
  const convs = [
    {
      id: "demo-conv-family-cohen",
      organization_id: ORG_ID,
      type: "family",
      subject: "About Noa",
      classroom_id: null,
      family_id: COHEN_FAMILY_ID,
      created_by: "demo-teacher",
      last_message_at: hoursAgo(2),
      created_at: hoursAgo(26),
      updated_at: hoursAgo(2),
      participants: ["demo-teacher", "demo-parent", "demo-admin"],
    },
    {
      id: "demo-conv-classroom-sunflower",
      organization_id: ORG_ID,
      type: "classroom",
      subject: "Sunflower classroom",
      classroom_id: SUNFLOWER_ID,
      family_id: null,
      created_by: "demo-teacher",
      last_message_at: hoursAgo(14),
      created_at: hoursAgo(14),
      updated_at: hoursAgo(14),
      participants: ["demo-teacher", "demo-parent", "demo-admin"],
    },
    {
      id: "demo-conv-school-announcement",
      organization_id: ORG_ID,
      type: "announcement",
      subject: "Fall enrollment is now open",
      classroom_id: null,
      family_id: null,
      created_by: "demo-admin",
      last_message_at: hoursAgo(30),
      created_at: hoursAgo(30),
      updated_at: hoursAgo(30),
      participants: ["demo-admin", "demo-parent", "demo-teacher"],
    },
  ];

  const messages = [
    {
      id: "demo-msg-1",
      conversation_id: "demo-conv-family-cohen",
      sender_id: "demo-teacher",
      body: "Hi! Noa had a lovely day today — 2 hour nap and really enjoyed the sensory table.",
      created_at: hoursAgo(26),
    },
    {
      id: "demo-msg-2",
      conversation_id: "demo-conv-family-cohen",
      sender_id: "demo-parent",
      body: "Thanks for the update. Did she finish her lunch?",
      created_at: hoursAgo(24),
    },
    {
      id: "demo-msg-3",
      conversation_id: "demo-conv-family-cohen",
      sender_id: "demo-teacher",
      body: "She did! All of her pasta and most of the strawberries. 🍓",
      created_at: hoursAgo(2),
    },
    {
      id: "demo-msg-4",
      conversation_id: "demo-conv-classroom-sunflower",
      sender_id: "demo-teacher",
      body:
        "Quick note for all Sunflower families: we'll be visiting the library tomorrow at 10:30. Please pack a library card if your child has one!",
      created_at: hoursAgo(14),
    },
    {
      id: "demo-msg-5",
      conversation_id: "demo-conv-school-announcement",
      sender_id: "demo-admin",
      body:
        "We're excited to announce that fall enrollment is now open. Please contact the office to discuss availability across Willow, Maple, and Acorn rooms.",
      created_at: hoursAgo(30),
    },
  ];

  // Track per-participant read state.
  const reads = {};
  convs.forEach((c) => {
    reads[c.id] = {};
    c.participants.forEach((p) => {
      // Everyone except the last sender has unread.
      reads[c.id][p] = minutesAgo(60 * 48); // "long ago"
    });
    const lastMsg = [...messages]
      .filter((m) => m.conversation_id === c.id)
      .sort((a, b) => b.created_at.localeCompare(a.created_at))[0];
    if (lastMsg) {
      reads[c.id][lastMsg.sender_id] = lastMsg.created_at;
    }
  });

  return { conversations: convs, messages, reads };
}

function readStore() {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    // fall through
  }
  const seeded = seedInitial();
  writeStore(seeded);
  return seeded;
}

function writeStore(store) {
  localStorage.setItem(STORE_KEY, JSON.stringify(store));
}

// -------------------------------------------------------------------------
// Public API (demo mode)
// -------------------------------------------------------------------------

/** Conversations visible to `profileId` with computed unread + last message. */
export function demoListConversations(profileId, { role } = {}) {
  const store = readStore();
  const visible = store.conversations.filter((c) => {
    // Admin sees all in org.
    if (role === "admin") return c.organization_id === ORG_ID;
    // cms_editor: none.
    if (role === "cms_editor") return false;
    // Everyone else: must be a participant (demo pre-seeds org-wide membership for
    // announcements + classroom/family membership too).
    return c.participants.includes(profileId);
  });
  return visible
    .map((c) => {
      const msgs = store.messages
        .filter((m) => m.conversation_id === c.id)
        .sort((a, b) => a.created_at.localeCompare(b.created_at));
      const lastMessage = msgs[msgs.length - 1] || null;
      const lastRead = store.reads?.[c.id]?.[profileId] || null;
      const unreadCount = msgs.filter(
        (m) =>
          m.sender_id !== profileId &&
          (!lastRead || m.created_at > lastRead),
      ).length;
      return {
        ...c,
        last_message: lastMessage,
        unread_count: unreadCount,
      };
    })
    .sort((a, b) =>
      (b.last_message_at || "").localeCompare(a.last_message_at || ""),
    );
}

export function demoGetConversation(conversationId, profileId) {
  const store = readStore();
  const c = store.conversations.find((x) => x.id === conversationId);
  if (!c) return null;
  const messages = store.messages
    .filter((m) => m.conversation_id === conversationId)
    .sort((a, b) => a.created_at.localeCompare(b.created_at));
  const lastRead = store.reads?.[c.id]?.[profileId] || null;
  return { ...c, messages, last_read_at: lastRead };
}

export function demoSendMessage({ conversationId, senderId, body }) {
  const store = readStore();
  const now = new Date().toISOString();
  const message = {
    id: `demo-msg-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    conversation_id: conversationId,
    sender_id: senderId,
    body,
    created_at: now,
  };
  store.messages.push(message);
  store.conversations = store.conversations.map((c) =>
    c.id === conversationId ? { ...c, last_message_at: now, updated_at: now } : c,
  );
  // Sender has implicitly read what they just sent.
  if (!store.reads[conversationId]) store.reads[conversationId] = {};
  store.reads[conversationId][senderId] = now;
  writeStore(store);
  if (senderId === "demo-parent") {
    try {
      localStorage.setItem(PARENT_ONBOARDING_MESSAGE_KEY, "1");
    } catch {
      // Demo persistence is best-effort.
    }
  }
  return message;
}

/** True only after the demo parent has sent a message during this preview. */
export function demoHasSentParentMessage(profileId) {
  if (profileId !== "demo-parent") return false;
  try {
    return localStorage.getItem(PARENT_ONBOARDING_MESSAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export function demoMarkRead({ conversationId, profileId }) {
  const store = readStore();
  if (!store.reads[conversationId]) store.reads[conversationId] = {};
  store.reads[conversationId][profileId] = new Date().toISOString();
  writeStore(store);
}

export function demoCreateConversation({
  type,
  subject,
  classroomId = null,
  familyId = null,
  createdBy,
  participantIds = [],
  firstMessageBody,
}) {
  const store = readStore();
  const now = new Date().toISOString();
  const id = `demo-conv-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
  const participants = Array.from(new Set([createdBy, ...participantIds]));
  const conv = {
    id,
    organization_id: ORG_ID,
    type,
    subject: subject || null,
    classroom_id: classroomId,
    family_id: familyId,
    created_by: createdBy,
    last_message_at: now,
    created_at: now,
    updated_at: now,
    participants,
  };
  store.conversations.push(conv);
  store.reads[id] = {};
  participants.forEach((p) => {
    store.reads[id][p] = p === createdBy ? now : null;
  });
  if (firstMessageBody) {
    const msg = {
      id: `demo-msg-${Date.now()}`,
      conversation_id: id,
      sender_id: createdBy,
      body: firstMessageBody,
      created_at: now,
    };
    store.messages.push(msg);
  }
  writeStore(store);
  return conv;
}
